import React, { useMemo, useState } from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import ProfileServiceCard from './ProfileServiceCard';
import ProfilePageCard from './ProfilePageCard';

// Cada sección muestra las primeras tarjetas; «Ver más (N)» abre las demás en la misma página (sin caja con su propia barra)
const VISIBLES = 3;

const SectionHeader = ({ title, subtitle, count, abierta, onToggle }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
    <Box>
      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
    {count > VISIBLES && (
      <Link
        component="button"
        variant="body2"
        onClick={onToggle}
        aria-expanded={abierta}
        sx={{
          color: 'primary.main',
          fontWeight: 'bold',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        {abierta ? 'Ver menos' : `Ver más (${count})`}
      </Link>
    )}
  </Box>
);

const ProfileServiceList = ({ profile, user }) => {
  const userName = user?.name || profile?.nombre || 'el proveedor';
  // Secciones abiertas con «Ver más» ('servicios', 'independientes', 'grupos')
  const [abiertas, setAbiertas] = useState({});
  const alternar = (seccion) => setAbiertas(prev => ({ ...prev, [seccion]: !prev[seccion] }));
  const primeras = (lista, seccion) => (abiertas[seccion] ? lista : lista.slice(0, VISIBLES));

  const { independentServices, independentPages, groupPages } = useMemo(() => {
    const services = profile?.servicios || [];
    const pages = profile?.paginas || [];
    
    // Servicios que ya salen en una página. Cada uno llega como { servicio, facilitadores }: se lee el id como en ProfilePageCard
    const servicesInPages = pages.reduce((acc, p) => {
      return [...acc, ...(p.servicios || []).map(ps => (ps?.servicio?._id || ps?.servicio || ps)?.toString())];
    }, []);

    // 1. Independent Services (not in any page)
    const indServices = services.filter(s => !servicesInPages.includes(s._id.toString()));

    // Grupo/negocio o independiente: el tipo de la página (un servidor sin actualizar no lo manda: se cuenta el equipo que aceptó)
    const esGrupo = p => (p.tipo ? p.tipo === 'grupo' : (p.miembros || []).filter(m => m.status === 1).length > 1);

    // 2. Independent Pages
    const indPages = pages.filter(p => !esGrupo(p));

    // 3. Group Pages
    const grPages = pages.filter(esGrupo);

    return {
      independentServices: indServices,
      independentPages: indPages,
      groupPages: grPages
    };
  }, [profile]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* SECTION 1: Independent Services */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Servicios" 
          subtitle={`que ${userName} brinda`}
          count={independentServices.length}
          abierta={Boolean(abiertas.servicios)}
          onToggle={() => alternar('servicios')}
        />
        <Stack spacing={2}>
          {independentServices.length > 0 ? (
            primeras(independentServices, 'servicios').map((service, index) => (
              <ProfileServiceCard
                key={service._id || index}
                service={service}
                user={user}
                profile={profile}
                imparte={service.imparte}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pb: 2 }}>
              No hay servicios independientes disponibles.
            </Typography>
          )}
        </Stack>
      </Box>

      {/* SECTION 2: Independent Pages */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Páginas de Servicios Independientes" 
          subtitle={`que ${userName} brinda y administra`}
          count={independentPages.length}
          abierta={Boolean(abiertas.independientes)}
          onToggle={() => alternar('independientes')}
        />
        <Stack spacing={2}>
          {independentPages.length > 0 ? (
            primeras(independentPages, 'independientes').map((page, index) => (
              <ProfilePageCard
                key={page._id || index}
                page={page}
                user={user}
                profile={profile}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pb: 2 }}>
              No hay páginas de servicios independientes disponibles.
            </Typography>
          )}
        </Stack>
      </Box>

      {/* SECTION 3: Group Pages */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Páginas de Grupos y Negocios" 
          subtitle={`de los que ${userName} es parte`}
          count={groupPages.length}
          abierta={Boolean(abiertas.grupos)}
          onToggle={() => alternar('grupos')}
        />
        <Stack spacing={2}>
          {groupPages.length > 0 ? (
            primeras(groupPages, 'grupos').map((page, index) => (
              <ProfilePageCard
                key={page._id || index}
                page={page}
                user={user}
                profile={profile}
                isGroup
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pb: 2 }}>
              No participa actualmente en grupos o negocios.
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ProfileServiceList;
